import React from 'react';
import MaterialTable, { MTableAction } from 'material-table';
import { createTheme, MuiThemeProvider } from '@material-ui/core/styles';

// Modals
import EhcEmployeeForm from './EmployeeForm';
import { bigDownload } from '../../plugins/downloader';

export default class EhcEmployeesDataTable extends React.Component {
  constructor(props) {
    super(props);
    this.tableRef = React.createRef();
    this.theme = createTheme({
      palette: {
        primary: {
          main: '#7cb342',
        },
        secondary: {
          main: '#4caf50',
        },
      },
    });

    this.downloadAttachmentLock = false; // Syncronous lock for handle function block.

    this.baseUrl = (company_id) => {
      if (!company_id) { // If company_id is not provided, use the one from props
        company_id = this.props.company_id;
      } else if (Array.isArray(company_id)) { // If company_id is an array, do not use company path
        company_id = null;
      }

      return `/api/ehc/${company_id ? `companies/${company_id}/` : ''}employees`;
    };

    this.formatISOToYMDHM = (isoDate) => {
      if (!isoDate) return '';
      const date = new Date(isoDate);

      const yyyy = date.getFullYear();
      const mm = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
      const dd = String(date.getDate()).padStart(2, '0');

      const hh = String(date.getHours()).padStart(2, '0');
      const min = String(date.getMinutes()).padStart(2, '0');

      return `${yyyy}-${mm}-${dd} ${hh}:${min}`;
    };

    // This function to try to get a nested field from an object
    // Usefull for custom render of Material Table that combined with grouping,
    // When grouping, custom render is used for displaying the data the param that passed is the field directly
    // It is assuming the fields of the data is not nested
    this.tryGetRowDataField = (input, field) => {
      if (input !== null && typeof input === "object") {
        return input[field];
      }
      return input;
    }

    // Arrange columns
    let baseColumns = [
      {
        title: 'Avatar', field: 'avatar_url', export: false, width: 20, grouping: false, sorting: false,
        render: rowData => (
          <a href={this.tryGetRowDataField(rowData, 'account_url')}>
            <img
              style={{ height: 36, borderRadius: '50%' }}
              src={rowData.avatar_url}
            />
          </a>
        ),
      },
      { title: 'Name', field: 'full_name', grouping: false,
        render: rowData => (
          <a href={this.tryGetRowDataField(rowData, 'account_url')}>
            {this.tryGetRowDataField(rowData, 'full_name')}
          </a>
        ),
      },
      { title: 'Email', field: 'email', grouping: false },
      { title: 'NIK', field: 'nik', grouping: false },
      { title: 'DOB', field: 'date_of_birth', grouping: false },
      { title: 'Gender', field: 'gender' },
      { title: 'Phone', field: 'mobile_phone', grouping: false },
      {
        title: 'Company', field: 'company_name', orderField: 'companies.name',
        render: rowData => (
          <a href={this.tryGetRowDataField(rowData, 'company_url')}>
            {this.tryGetRowDataField(rowData, 'company_name')}
          </a>
        ),
      },
      // { title: 'ID', field: 'employment_id_number', grouping: false },
      { title: 'Last Sign', field: 'current_sign_in_at', orderField: 'accounts.current_sign_in_at', searchable: false, grouping: false },
      { title: 'Register At', field: 'created_at', searchable: false, grouping: false },
    ]

    // Arrange action
    let baseActions = [
      {
        icon: 'refresh',
        tooltip: 'Refresh Data',
        isFreeAction: true,
        onClick: () =>
          this.tableRef.current && this.tableRef.current.onQueryChange(),
      },
    ]

    // When editable
    if (this.props.editable) {
      baseActions.push({
        icon: 'edit',
        tooltip: 'Edit',
        position: 'row',
        onClick: (evt, selectedRow) => this.handleOpenEmployeeEditModal(selectedRow)
      });
    }

    // When removable
    if (this.props.removable) {
    }

    baseActions.push({
      icon: 'file_download',
      tooltip: 'Download All Checkup Results',
      position: 'row',
      onClick: (evt, selectedRow) => this.downloadAttachments(selectedRow),
    });

    this.state = {
      columns: baseColumns,
      actions: baseActions,
      data: [],
      page: null,
      open: false,
      totalCount: null,

      // Action state
      isDownloadingAttachment: false, // state lock for update rendering

      // Modal state
      employeeEditModalOpen: false,
      editedEmployee: null
    };
  }

  // =====================
  // Modal Handlers
  // =====================
  handleOpenEmployeeEditModal = async (employee) => {
    this.setState({
      employeeEditModalOpen: true,
      editedEmployee: employee
    });
  };

  handleCloseModal = () => {
    this.setState({
      employeeEditModalOpen: false,
      editedEmployee: null
    });
  };e

  // =====================
  // MaterialTable Queries
  // =====================
  getApiErrorMessage(result) {
    if (result.success) {
      return null;
    }
    if (result.errors && typeof result.errors === 'object' && !Array.isArray(result.errors)) {
      // handle validation error
      return 'Validation: ' + Object.entries(result.errors).map(([key, value]) => `${key}: ${value}`).join(', ');
    }
    console.error('Error fetching data:', result);
    if (result.message) {
      return result.message;
    }
    return 'There is something wrong';
  }

  async downloadAttachments(employee) {
    if (!employee) return;

    await bigDownload(
      `${this.baseUrl(employee.company_id)}/${employee.id}/download_attachments`,
      () => {
        if (this.downloadAttachmentLock) return false;

        this.downloadAttachmentLock = true;
        this.setState({ isDownloadingAttachment: true });
        return true;
      },
      () => {
        this.downloadAttachmentLock = false;
        this.setState({ isDownloadingAttachment: false });
        return true;
      }
    );
  }

  fetchData(query) {
    return new Promise((resolve, reject) => {
      const requestedPage = query.page + 1;

      console.log('Query details:', {
        page: query.page,
        pageSize: query.pageSize,
        requestedPage: requestedPage,
        search: query.search
      });

      const baseParams = 'per_page=' + query.pageSize
        + (this.props.service_order_id ? '&service_order_id=' + this.props.service_order_id : '')
        + '&q=' + query.search
        + '&sort_by=' + (query.orderBy?.orderField ?? query.orderBy?.field ?? '')
        + '&sort_order=' + (query.orderDirection ?? 'asc');
      const url = this.baseUrl() + '?' + baseParams + '&page=' + requestedPage;

      fetch(url)
        .then(async res => {
          if (res && res.status === 200 && res.ok) {
            return res.json();
          } else {
            console.log('Error response, fetching fallback data');
            const lastPageUrl = this.baseUrl() + '?' + baseParams + '&page=1';
            const fallbackRes = await fetch(lastPageUrl);
            const fallbackData = await fallbackRes.json();
            const lastPage = fallbackData.pagination.last;

            console.log('Fallback - redirecting to last page:', lastPage);
            const correctedUrl = this.baseUrl() + '?' + baseParams + '&page=' + lastPage;
            const correctedRes = await fetch(correctedUrl);
            return await correctedRes.json();
          }
        })
        .then(data => {
          console.log('Fetch response:', data);

          const currentPage = data.pagination.page - 1;
          const totalCount = data.pagination.count;
          const actualPageSize = parseInt(data.pagination.vars.items) || query.pageSize;
          const lastPage = data.pagination.last - 1;

          console.log('Pagination calculation:', {
            currentPage,
            totalCount,
            actualPageSize,
            lastPage,
            requestedPage: query.page,
            queryPageSize: query.pageSize
          });

          let adjustedTotalCount = totalCount;
          if (query.page > lastPage) {

            adjustedTotalCount = (lastPage + 1) * query.pageSize;
            console.log('Adjusted total count to prevent overflow:', adjustedTotalCount);
          }

          resolve({
            data: data.employees,
            page: currentPage,
            totalCount: adjustedTotalCount
          });
        })
        .catch(error => {
          console.error('Fetch error:', error);
          reject(error);
        });
    });
  }

  // =====================
  // Render Modal
  // =====================
  renderModals() {
    return <>
      {(this.props.editable || this.props.removable) &&
        <EhcEmployeeForm
          employee={this.state.editedEmployee}
          hospitals={this.props.hospitals}
          editable={this.props.editable}
          removable={this.props.removable}
          tableRef={this.tableRef}
          open={this.state.employeeEditModalOpen}
          onClose={this.handleCloseModal}
          onRemove={() => this.handleOpenEmployeeOrderRemovalModal([this.state.editedEmployee])}
        ></EhcEmployeeForm>
      }
    </>
  }

  // =====================
  // Render Component
  // =====================
  render() {
    return (
      <MuiThemeProvider theme={this.theme}>

        {this.renderModals()}

        <MaterialTable
          title={this.props.title + (this.state.isDownloadingAttachment ? ' (Downloading attachments...)' : '')}
          columns={this.state.columns}
          tableRef={this.tableRef}
          actions={this.state.actions}
          options={{
            pageSize: 20,
            pageSizeOptions: [20, 30, 50],
            exportButton: true,
            sorting: true,
            search: true,
            grouping: true,
            addRowPosition: 'first',
            // Force MaterialTable to respect the pagination boundaries
            emptyRowsWhenPaging: false,
            // This will help ensure proper pagination behavior
            paginationType: 'normal'
          }}
          components={{
            Actions: props => (<>
              {props.actions && props.actions.length > 0 &&
                props.actions
                .filter(action =>
                  !action.hidden &&
                  (!action.hideFunc || !action.hideFunc(props.data)))
                .map((action, index) =>
                  <MTableAction
                  key={index}
                  action={action}
                  data={props.data}
                  size={props.size}
                  disabled={
                    props.disabled ||
                    action.disabled ||
                    (action.disableFunc && action.disableFunc(props.data))
                  }
                />
              )}
            </>)
          }}
          data={query =>
            this.fetchData(query).then(result => {
              // Update component state to track current pagination
              this.setState({
                currentPage: result.page,
                totalCount: result.totalCount
              });
              return result;
            })
          }
        />
        <span style={{ color: 'red' }} hidden={!this.state.error}>* { this.state.error }</span>
      </MuiThemeProvider>
    );
  }
}
